import { useAuth } from '@/stores/AuthContext';
import { Button, Form, Input, Modal, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

interface RegisterDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface RegisterFormValues {
  email: string;
  name: string;
  referralCode: string;
  code: string;
}

const VERIFICATION_CODE_LENGTH = 6;
const COUNTDOWN_DURATION = 60; // 60 seconds

export const RegisterDialog = observer(
  ({ open, onOpenChange }: RegisterDialogProps) => {
    const [form] = Form.useForm<RegisterFormValues>();
    const [isSending, setIsSending] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const { sendRegisterCode, verifyRegisterCode } = useAuth();

    useEffect(() => {
      let timer: NodeJS.Timeout;
      if (countdown > 0) {
        timer = setInterval(() => {
          setCountdown((prev) => prev - 1);
        }, 1000);
      }
      return () => {
        if (timer) clearInterval(timer);
      };
    }, [countdown]);

    const handleSendEmail = async () => {
      if (isSending || countdown > 0) return;

      const values = form.getFieldsValue(['email', 'name', 'referralCode']);
      if (!values.email || !values.name || !values.referralCode) {
        message.error('Please fill in all required fields');
        return;
      }

      setIsSending(true);
      try {
        await sendRegisterCode(values.email, values.name, values.referralCode);
        setCountdown(COUNTDOWN_DURATION);
        message.success({
          content: 'Verification code sent to email',
          key: 'verification-email-sent',
        });
      } catch (error) {
        message.error({
          content: `Failed to send verification code: ${error}`,
          key: 'send-verification-error',
        });
      } finally {
        setIsSending(false);
      }
    };

    const handleVerifyCode = useCallback(
      async (values: RegisterFormValues) => {
        if (isLoading) return;

        setIsLoading(true);
        try {
          await verifyRegisterCode(values.email, values.code);
          onOpenChange?.(false);
          form.resetFields();
          message.success({
            content: 'You are now registered',
            key: 'register-success',
          });
        } catch (error) {
          form.setFieldValue('code', ''); // Clear the invalid code
          message.error({
            content: `Failed to verify code: ${error}`,
            key: 'verify-code-error',
          });
        } finally {
          setIsLoading(false);
        }
      },
      [form, isLoading, verifyRegisterCode, onOpenChange],
    );

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value.replace(/[^0-9]/g, '');
      if (newValue.length <= VERIFICATION_CODE_LENGTH) {
        form.setFieldValue('code', newValue);
        if (newValue.length === VERIFICATION_CODE_LENGTH && !isLoading) {
          const values = form.getFieldsValue();
          handleVerifyCode(values);
        }
      }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      return () => {
        form.resetFields();
      };
    }, [open]);

    return (
      <>
        <Modal
          title="Register"
          open={open}
          onCancel={() => onOpenChange?.(false)}
          footer={null}
          width={425}
          maskClosable={false}
        >
          <div className="pb-4">
            <p className="text-sm text-gray-500 mb-4">Create a new account</p>
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              onFinish={handleVerifyCode}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input
                  placeholder="Enter your email"
                  disabled={isSending || isLoading}
                  type="email"
                />
              </Form.Item>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input
                  placeholder="Enter your name"
                  disabled={isSending || isLoading}
                />
              </Form.Item>
              <Form.Item
                name="referralCode"
                label="Referral Code"
                rules={[
                  {
                    required: true,
                    message: 'Please enter your referral code',
                  },
                ]}
              >
                <Input
                  placeholder="Enter your referral code"
                  disabled={isSending || isLoading}
                />
              </Form.Item>
              <Form.Item
                name="code"
                label="Verification Code"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the verification code',
                  },
                  {
                    len: VERIFICATION_CODE_LENGTH,
                    message: `Code must be ${VERIFICATION_CODE_LENGTH} digits`,
                  },
                ]}
              >
                <div className="flex gap-2">
                  <Input
                    onChange={handleCodeChange}
                    placeholder="Enter verification code"
                    disabled={isSending || isLoading}
                    maxLength={VERIFICATION_CODE_LENGTH}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                  />
                  <Button
                    onClick={handleSendEmail}
                    loading={isSending}
                    disabled={isLoading || countdown > 0}
                  >
                    {countdown > 0 ? `${countdown}s` : 'Send'}
                  </Button>
                </div>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  disabled={isSending}
                  className="w-full"
                >
                  Register
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Modal>
      </>
    );
  },
);
